import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, RefreshCw, Loader2, Bookmark, LayoutGrid, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";

import {
  fetchCategories,
  fetchRandomVerse,
  fetchDailyVerse,
  localDateISO,
} from "@/lib/api";
import {
  getFavorites,
  isFavorite,
  toggleFavorite as toggleFavoriteStorage,
  removeFavorite,
} from "@/lib/favorites";
import { CategoryChips } from "@/components/CategoryChips";
import { CategoryMenu } from "@/components/CategoryMenu";
import { VerseCard } from "@/components/VerseCard";
import { DailyView } from "@/components/DailyView";
import { HomeMenu } from "@/components/HomeMenu";
import { BiblePageBackground } from "@/components/BiblePageBackground";
import { ShareCard } from "@/components/ShareCard";
import { ModeSwitch } from "@/components/ModeSwitch";
import { FavoritesView } from "@/components/FavoritesView";
import { JournalModal } from "@/components/JournalModal";
import { JourneyView } from "@/components/JourneyView";
import { getEntries, addEntry, removeEntry } from "@/lib/journal";

const BG_IMAGES = [
  "https://images.unsplash.com/photo-1763928801168-c4801758c5d0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  "https://images.unsplash.com/photo-1772556318844-e6d2ec48ae35?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  "https://images.unsplash.com/photo-1770650776273-a62fa170355b?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
];
const pickBackground = () => BG_IMAGES[Math.floor(Math.random() * BG_IMAGES.length)];

// Deterministic background per date so daily-mode feels stable.
const dailyBackground = (iso) => {
  let h = 0;
  for (let i = 0; i < iso.length; i++) h = (h * 31 + iso.charCodeAt(i)) >>> 0;
  return BG_IMAGES[h % BG_IMAGES.length];
};

const formatDateNice = (iso) => {
  // iso is YYYY-MM-DD; build a Date in local time so "today" reads correctly.
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export default function Wallpaper() {
  const [mode, setMode] = useState("daily"); // "daily" | "explore"
  const [showFavorites, setShowFavorites] = useState(false);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showJourney, setShowJourney] = useState(false);
  const [journalVerse, setJournalVerse] = useState(null); // open when set
  const [entries, setEntries] = useState(() => getEntries());
  const [showHomeMenu, setShowHomeMenu] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalVerses, setTotalVerses] = useState(0);
  const [activeCategory, setActiveCategory] = useState("all");
  const [verse, setVerse] = useState(null);
  const [bgUrl, setBgUrl] = useState(pickBackground());
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  // tap-gating: unlocks only after the reflection finishes its reveal
  const [readyForTap, setReadyForTap] = useState(false);

  // Start a ready timer whenever a new verse is shown.
  // delay (2.2) + duration (1.1) + small buffer = ~3.4s
  useEffect(() => {
    if (!verse) return;
    setReadyForTap(false);
    const id = setTimeout(() => setReadyForTap(true), 3400);
    return () => clearTimeout(id);
  }, [verse?.reference, verse?.translation]);

  // favorites state (mirrors localStorage)
  const [favs, setFavs] = useState(() => getFavorites());
  const favorited = isFavorite(verse, favs);

  // local date — re-checked every minute so daily verse rolls at midnight
  const [today, setToday] = useState(localDateISO());

  const shareRef = useRef(null);

  // ---- Loading helpers ----
  const loadDaily = useCallback(async (iso) => {
    setLoading(true);
    try {
      const v = await fetchDailyVerse(iso);
      setVerse(v);
      setBgUrl(dailyBackground(iso));
    } catch {
      toast.error("Couldn’t load today’s verse.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRandom = useCallback(
    async (category = activeCategory, currentRef = null) => {
      setLoading(true);
      try {
        const v = await fetchRandomVerse({
          category,
          excludeReference: currentRef,
        });
        setVerse(v);
        setBgUrl(pickBackground());
      } catch {
        toast.error("Couldn’t load a verse. Try again.");
      } finally {
        setLoading(false);
      }
    },
    [activeCategory]
  );

  // Initial load — categories + first verse based on default mode (daily)
  useEffect(() => {
    (async () => {
      try {
        const cats = await fetchCategories();
        setCategories(cats.categories || []);
        setCategoryCounts(cats.counts || {});
        setTotalVerses(cats.total_verses || 0);
      } catch {
        /* non-blocking */
      }
      await loadDaily(today);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch local date — refresh daily verse at midnight
  useEffect(() => {
    const id = setInterval(() => {
      const now = localDateISO();
      if (now !== today) {
        setToday(now);
        if (mode === "daily") loadDaily(now);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [today, mode, loadDaily]);

  // ---- Mode handling ----
  const handleModeChange = async (next) => {
    if (next === mode) return;
    setMode(next);
    if (next === "daily") {
      await loadDaily(today);
    } else {
      setActiveCategory("all");
      await loadRandom("all", null);
    }
  };

  // ---- Category (explore only) ----
  const handleCategorySelect = async (cat) => {
    if (cat === activeCategory) return;
    setActiveCategory(cat);
    await loadRandom(cat, null);
  };

  const handleCategoryFromMenu = async (cat) => {
    setShowCategoryMenu(false);
    if (cat === activeCategory) return;
    setActiveCategory(cat);
    await loadRandom(cat, null);
  };

  // ---- Tap to refresh (explore only, gated) ----
  const handleTapRefresh = () => {
    if (mode !== "explore") return;
    if (loading || sharing || !readyForTap) return;
    loadRandom(activeCategory, verse?.reference || null);
  };

  // ---- Favorites ----
  const handleToggleFavorite = (v) => {
    const next = toggleFavoriteStorage(v);
    setFavs(next);
    const wasAdded = next.some(
      (f) => f.reference === v.reference && f.translation === v.translation
    );
    toast.success(wasAdded ? "Saved to favourites." : "Removed from favourites.");
  };

  const handleRemoveFavorite = (v) => {
    setFavs(removeFavorite(v));
  };

  // ---- Journal ----
  const handleOpenJournal = (v) => {
    setJournalVerse(v);
  };

  const handleSaveJournal = (text) => {
    if (!journalVerse) return;
    const next = addEntry({ verse: journalVerse, entry: text });
    setEntries(next);
    setJournalVerse(null);
    toast.success("Reflection saved to your Journey.");
  };

  const handleRemoveEntry = (id) => {
    setEntries(removeEntry(id));
  };

  const openVerseFromFavorites = (v) => {
    setShowFavorites(false);
    setMode("explore");
    setVerse({ ...v });
    setBgUrl(pickBackground());
  };

  // ---- Share today's word (Daily) — text-only via Web Share API ----
  const handleShareDaily = async (v) => {
    if (!v) return;
    const title = `Lifted Daily · ${formatDateNice(today)}`;
    const body = `"${v.text}"\n— ${v.reference}\n\n${v.explanation}\n\n— shared from Lifted Daily`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: body });
        return;
      }
    } catch {
      /* user dismissed — fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Copied today's word to your clipboard.");
    } catch {
      toast.error("Couldn’t share. Try long-pressing the verse to copy.");
    }
  };

  // ---- Share as image ----
  const handleShare = async (e) => {
    e.stopPropagation();
    if (!verse || !shareRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await htmlToImage.toPng(shareRef.current, {
        cacheBust: true,
        pixelRatio: 1,
      });
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File(
          [blob],
          `${verse.reference.replace(/\s+/g, "_")}.png`,
          { type: "image/png" }
        );
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: verse.reference,
            text: `"${verse.text}" — ${verse.reference}`,
          });
          toast.success("Shared.");
          return;
        }
      } catch {
        /* fall through to download */
      }
      const link = document.createElement("a");
      link.download = `${verse.reference.replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Image saved.");
    } catch {
      toast.error("Couldn’t create image.");
    } finally {
      setSharing(false);
    }
  };

  const isDaily = mode === "daily";

  return (
    <div
      className={`relative w-full select-none ${isDaily ? "min-h-dvh" : "h-dvh overflow-hidden"}`}
      onClick={handleTapRefresh}
      style={{ cursor: mode === "explore" && readyForTap ? "pointer" : "default" }}
      data-testid="wallpaper-root"
    >
      {/* Background — aged Bible page on every screen for consistency */}
      <div className="absolute inset-0 ld-anim-bg">
        <BiblePageBackground />
      </div>

      {/* Paper grain — Explore only (Daily uses its own subtler texture) */}
      {!isDaily && (
        <div className="absolute inset-0 paper-grain z-[2] pointer-events-none" />
      )}

      {/* Top — Explore mode only (Daily home stays sacred / chrome-free) */}
      {!isDaily && (
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="flex items-center justify-between px-6 pt-5 md:pt-6 pb-1">
            <span
              className="font-ui text-sm md:text-base uppercase tracking-[0.36em] text-white font-bold"
              style={{
                textShadow:
                  "0 1px 2px rgba(40,28,12,0.85), 0 0 10px rgba(40,28,12,0.6), 0 0 18px rgba(40,28,12,0.4)",
              }}
            >
              Lifted · Daily
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleModeChange("daily");
                }}
                data-testid="goto-today"
                className="font-ui text-[10px] uppercase tracking-[0.32em] text-[#96918A] hover:text-[#4A5D4E] transition-colors"
              >
                Today
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowJourney(true);
                }}
                data-testid="open-journey"
                aria-label="Open journey"
                className="ld-icon-chip"
              >
                <NotebookPen className="w-[24px] h-[24px]" strokeWidth={1.8} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFavorites(true);
                }}
                data-testid="open-favorites"
                aria-label="Open favourites"
                className="ld-icon-chip -mr-1"
              >
                <Bookmark className="w-[24px] h-[24px]" strokeWidth={1.8} />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center pt-1 pb-2 gap-2">
            <ModeSwitch mode={mode} onChange={handleModeChange} />
            <div className="flex items-center gap-1 w-full max-w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCategoryMenu(true);
                }}
                data-testid="open-category-menu"
                aria-label="All categories"
                className="shrink-0 ml-5 ld-icon-chip"
              >
                <LayoutGrid className="w-[22px] h-[22px]" strokeWidth={1.8} />
              </button>
              <div className="flex-1 min-w-0">
                <CategoryChips
                  categories={categories}
                  active={activeCategory}
                  onSelect={handleCategorySelect}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Center — verse */}
      <div className={`relative z-10 flex flex-col h-full ${isDaily ? "" : "pt-44 md:pt-48 pb-28"}`}>
        {isDaily ? (
          <DailyView
            verse={verse}
            onOpenMenu={() => setShowHomeMenu(true)}
            isFavorited={favorited}
            onToggleFavorite={handleToggleFavorite}
            onShare={handleShareDaily}
          />
        ) : (
          <VerseCard
            verse={verse}
            isFavorited={favorited}
            onToggleFavorite={handleToggleFavorite}
            onOpenJournal={handleOpenJournal}
            compact={false}
          />
        )}
      </div>

      {/* Bottom — Explore only: gentle hint + share/refresh */}
      {!isDaily && (
        <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-6 pb-8 md:pb-10">
          <div className="min-h-[14px]" data-testid="tap-hint-wrap">
            <AnimatePresence>
              {readyForTap && !loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2 }}
                  className="font-ui text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#96918A]"
                  data-testid="tap-hint"
                >
                  Tap when you’re ready
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={(e) => {
                e.stopPropagation();
                if (!readyForTap || loading) return;
                loadRandom(activeCategory, verse?.reference || null);
              }}
              disabled={loading || !readyForTap}
              data-testid="refresh-button"
              aria-label="New verse"
              className="rounded-full p-3 bg-[#F5F3ED]/70 backdrop-blur-md border border-[#E5E1D8] text-[#4A5D4E] hover:bg-[#EAE6DF] transition-colors disabled:opacity-40"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={handleShare}
              disabled={!verse || sharing}
              data-testid="share-button"
              aria-label="Share as image"
              className="rounded-full p-3 bg-[#4A5D4E] text-[#F5F3ED] hover:bg-[#3B4C3F] transition-colors disabled:opacity-60"
            >
              {sharing ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Share2 className="w-4 h-4" strokeWidth={1.5} />
              )}
            </motion.button>
          </div>
        </div>
      )}

      {/* Daily home menu sheet (Explore / Favourites / Journey) — opened by tapping the FOR TODAY header */}
      <HomeMenu
        open={showHomeMenu}
        onClose={() => setShowHomeMenu(false)}
        onExplore={() => {
          setShowHomeMenu(false);
          handleModeChange("explore");
        }}
        onFavorites={() => {
          setShowHomeMenu(false);
          setShowFavorites(true);
        }}
        onJourney={() => {
          setShowHomeMenu(false);
          setShowJourney(true);
        }}
      />

      {/* Favourites overlay */}
      <AnimatePresence>
        {showFavorites && (
          <FavoritesView
            favorites={favs}
            onClose={() => setShowFavorites(false)}
            onOpen={openVerseFromFavorites}
            onRemove={handleRemoveFavorite}
          />
        )}
      </AnimatePresence>

      {/* Category menu overlay */}
      <AnimatePresence>
        {showCategoryMenu && (
          <CategoryMenu
            categories={categories}
            counts={categoryCounts}
            totalVerses={totalVerses}
            active={activeCategory}
            onSelect={handleCategoryFromMenu}
            onClose={() => setShowCategoryMenu(false)}
          />
        )}
      </AnimatePresence>

      {/* Journey overlay */}
      <AnimatePresence>
        {showJourney && (
          <JourneyView
            entries={entries}
            onClose={() => setShowJourney(false)}
            onRemove={handleRemoveEntry}
          />
        )}
      </AnimatePresence>

      {/* Journal modal (write a reflection) */}
      <AnimatePresence>
        {journalVerse && (
          <JournalModal
            verse={journalVerse}
            onClose={() => setJournalVerse(null)}
            onSave={handleSaveJournal}
          />
        )}
      </AnimatePresence>

      {/* Off-screen export card */}
      <ShareCard ref={shareRef} verse={verse} backgroundUrl={bgUrl} />
    </div>
  );
}
